import { useState, useRef, useEffect } from "react";
import { Check } from "lucide-react";
import clsx from "clsx";

// Curated palette — 24 colors that work well on dark backgrounds.
// Trading semantics in row 1, then a spectrum for indicators/drawings.
export const COLOR_PALETTE = [
  // Trading reds
  "#ef5350", "#ff5252", "#d32f2f",
  // Oranges / amber
  "#ff8a65", "#ff9800", "#ffb74d",
  // Yellows
  "#ffd54f", "#fff176", "#cddc39",
  // Greens
  "#aed581", "#66bb6a", "#26a69a",
  // Cyans / blues
  "#00bcd4", "#4fc3f7", "#2962ff",
  // Purples
  "#5c6bc0", "#9575cd", "#ba68c8",
  // Pinks
  "#f06292", "#ec407a",
  // Neutrals
  "#d1d4dc", "#787b86", "#ffffff", "#000000",
];

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  /** Size of the swatch button in pixels (default 16) */
  size?: number;
  /** Optional aria-label override */
  ariaLabel?: string;
  /** Where the popover opens — auto-positions if not specified */
  popoverAlign?: "left" | "right";
}

export function ColorPicker({
  value,
  onChange,
  size = 16,
  ariaLabel = "Choose color",
  popoverAlign = "left",
}: ColorPickerProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={ariaLabel}
        title={ariaLabel}
        className="block rounded border border-border-strong/60 transition-transform hover:scale-110 focus:outline-none focus:ring-1 focus:ring-accent"
        style={{ backgroundColor: value, width: size, height: size }}
      />

      {open && (
        <div
          className={clsx(
            "absolute z-40 mt-1.5 rounded-md border border-border bg-bg-elevated p-2 shadow-elevated animate-slide-up",
            popoverAlign === "right" ? "right-0" : "left-0"
          )}
        >
          <div className="grid grid-cols-6 gap-1">
            {COLOR_PALETTE.map((c) => {
              const selected = c.toLowerCase() === value.toLowerCase();
              return (
                <button
                  key={c}
                  onClick={() => {
                    onChange(c);
                    setOpen(false);
                  }}
                  aria-label={`Color ${c}`}
                  className={clsx(
                    "flex h-5 w-5 items-center justify-center rounded-sm border transition-transform hover:scale-125",
                    selected ? "border-white" : "border-black/20"
                  )}
                  style={{ backgroundColor: c }}
                >
                  {selected && (
                    <Check
                      className="h-3 w-3"
                      style={{ color: isLightColor(c) ? "#000" : "#fff" }}
                      strokeWidth={3}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/** Crude luminance test so the check mark stays visible on light swatches. */
function isLightColor(hex: string): boolean {
  const c = hex.replace("#", "");
  if (c.length !== 6) return false;
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  // Perceptual luminance
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6;
}
